'use client';

import { HStack, Box } from '@terra/ui-styled-system';
import { Input } from '../../../components/input';
import { Tooltip } from '../../../components/tooltip';
import { Text } from '../../../components/styled/text';
import { SourceCodeIcon, Globe02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { TraceSourceFilter } from './TraceViewSettingsContext';

export type TraceViewMode = 'full' | 'minimized';
export type TraceViewToolbarProps = {
  viewMode: TraceViewMode;
  onViewModeChange: (mode: TraceViewMode) => void;
  showHttpOnly: boolean;
  onShowHttpOnlyChange: (show: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  showSearch?: boolean;
  toolbarExtra?: ReactNode;
  showStoryControls?: boolean;
  sourceFilter?: TraceSourceFilter;
  onSourceFilterChange?: (filter: TraceSourceFilter) => void;
  showHiddenSteps?: boolean;
  onShowHiddenStepsChange?: (show: boolean) => void;
  isReplaying?: boolean;
  onReplayToggle?: () => void;
};

function StoryToolbarToggleButton({
  label,
  tooltip,
  isActive,
  onClick,
}: {
  label: string;
  tooltip: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={tooltip}>
      <Box
        as="button"
        aria-label={label}
        onClick={onClick}
        h="7"
        px="2"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        borderWidth="1px"
        borderColor="border.default"
        borderRadius="md"
        bg={isActive ? 'bg.muted' : 'transparent'}
        color={isActive ? 'fg.default' : 'fg.muted'}
        _hover={{ bg: isActive ? 'bg.muted' : 'bg.hover' }}
      >
        <Text fontSize="xs" fontWeight="medium">
          {label}
        </Text>
      </Box>
    </Tooltip>
  );
}

export function TraceViewToolbar({
  viewMode,
  onViewModeChange,
  showHttpOnly,
  onShowHttpOnlyChange,
  searchQuery,
  onSearchQueryChange,
  showSearch = true,
  toolbarExtra,
  showStoryControls = false,
  sourceFilter = 'all',
  onSourceFilterChange,
  showHiddenSteps = false,
  onShowHiddenStepsChange,
  isReplaying = false,
  onReplayToggle,
}: TraceViewToolbarProps) {
  const t = useTranslations('designSystem.signals.drawer.traceView.viewMode');

  return (
    <HStack
      gap={1}
      alignItems="center"
      justifyContent="flex-end"
      w="fit-content"
      minW={0}
      flexWrap="nowrap"
      overflowX="auto"
    >
      <Box w="160px" flexShrink={0}>
        {showSearch && (
          <Input
            size="sm"
            placeholder={t('searchPlaceholder') || 'Search'}
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            w="full"
            h="7"
            minH="7"
            fontSize="xs"
            borderRadius="md"
          />
        )}
      </Box>

      <HStack gap={1} alignItems="center" flexShrink={0}>
        {showStoryControls && onSourceFilterChange ? (
          <HStack
            gap="0"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            overflow="hidden"
            flexShrink={0}
            h="7"
          >
            {(
              [
                ['all', t('sourceAll')],
                ['neo', t('sourceNeo')],
                ['co-pilot', t('sourceCoPilot')],
              ] as const
            ).map(([value, label]) => (
              <Tooltip key={value} title={t('sourceFilterTooltip')}>
                <Box
                  as="button"
                  aria-label={label}
                  onClick={() => onSourceFilterChange(value)}
                  h="full"
                  px="2"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  bg={sourceFilter === value ? 'bg.muted' : 'transparent'}
                  color={sourceFilter === value ? 'fg.default' : 'fg.muted'}
                  _hover={{
                    bg: sourceFilter === value ? 'bg.muted' : 'bg.hover',
                  }}
                >
                  <Text fontSize="xs" fontWeight="medium">
                    {label}
                  </Text>
                </Box>
              </Tooltip>
            ))}
          </HStack>
        ) : null}

        {showStoryControls && onShowHiddenStepsChange ? (
          <StoryToolbarToggleButton
            label={t('showHidden')}
            tooltip={t('showHiddenTooltip')}
            isActive={showHiddenSteps}
            onClick={() => onShowHiddenStepsChange(!showHiddenSteps)}
          />
        ) : null}

        {showStoryControls && onReplayToggle ? (
          <StoryToolbarToggleButton
            label={isReplaying ? t('stopReplay') : t('replay')}
            tooltip={t('replayTooltip')}
            isActive={isReplaying}
            onClick={onReplayToggle}
          />
        ) : null}

        <HStack
          gap="0"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          overflow="hidden"
          flexShrink={0}
          h="7"
        >
          <Tooltip title={t('fullTooltip')}>
            <Box
              as="button"
              aria-label={t('full')}
              onClick={() => onViewModeChange('full')}
              h="full"
              px="2"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={viewMode === 'full' ? 'bg.muted' : 'transparent'}
              color={viewMode === 'full' ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: viewMode === 'full' ? 'bg.muted' : 'bg.hover',
              }}
            >
              <Text fontSize="xs" fontWeight="medium">
                {t('full')}
              </Text>
            </Box>
          </Tooltip>
          <Tooltip title={t('condensedTooltip')}>
            <Box
              as="button"
              aria-label={t('minimized')}
              onClick={() => onViewModeChange('minimized')}
              h="full"
              px="2"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={viewMode === 'minimized' ? 'bg.muted' : 'transparent'}
              color={viewMode === 'minimized' ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: viewMode === 'minimized' ? 'bg.muted' : 'bg.hover',
              }}
            >
              <Text fontSize="xs" fontWeight="medium">
                {t('minimized')}
              </Text>
            </Box>
          </Tooltip>
        </HStack>

        <HStack
          gap="0"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          overflow="hidden"
          flexShrink={0}
          h="7"
        >
          <Tooltip title={t('allToolsTooltip')}>
            <Box
              as="button"
              aria-label={t('toggleCode')}
              onClick={() => onShowHttpOnlyChange(false)}
              h="full"
              w="7"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={!showHttpOnly ? 'bg.muted' : 'transparent'}
              color={!showHttpOnly ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: !showHttpOnly ? 'bg.muted' : 'bg.hover',
              }}
            >
              <SourceCodeIcon size={14} />
            </Box>
          </Tooltip>
          <Tooltip title={t('httpOnlyTooltip')}>
            <Box
              as="button"
              aria-label={t('toggleHttp')}
              onClick={() => onShowHttpOnlyChange(true)}
              h="full"
              w="7"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              bg={showHttpOnly ? 'bg.muted' : 'transparent'}
              color={showHttpOnly ? 'fg.default' : 'fg.muted'}
              _hover={{
                bg: showHttpOnly ? 'bg.muted' : 'bg.hover',
              }}
            >
              <Globe02Icon size={14} />
            </Box>
          </Tooltip>
        </HStack>

        {toolbarExtra}
      </HStack>
    </HStack>
  );
}
